import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Table } from 'react-bootstrap';
import { Check2, Copy, EnvelopeAt, GeoAlt, Globe2, Link45deg, Linkedin, PencilSquare, Search, Telephone } from 'react-bootstrap-icons';
import {
  useGetDomainsQuery,
  useGetEmailAccountsQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
} from '../services/api';
import { useAccount } from '../contexts/AccountContext';
import { preferencesTenantWriteSpread, useTenantIdForPreferences } from '../hooks/useTenantIdForPreferences';
import EMMSectionHeader from './EMMSectionHeader';

type AgentPurpose = 'cold_outreach' | 'transactional' | 'newsletter';
type AgentCardViewMode = 'compact' | 'detailed' | 'list';
type AgentSection = 'sales' | 'account_manager' | 'system';

interface AgentProfile {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  title?: string;
  linkedInUrl?: string;
  websiteUrl?: string;
  socialUrl?: string;
  phone?: string;
  location?: string;
  bio?: string;
}

interface AgentCard {
  email: string;
  domain: string;
  avatarUrl: string;
  purpose: AgentPurpose;
  section: AgentSection;
  fullName: string;
  profile: AgentProfile;
}

const EMPTY_PROFILE: AgentProfile = {
  firstName: '',
  lastName: '',
  fullName: '',
  title: '',
  linkedInUrl: '',
  websiteUrl: '',
  socialUrl: '',
  phone: '',
  location: '',
  bio: '',
};

const normalizeEmail = (value: unknown): string => String(value || '').trim().toLowerCase();
const normalizeDomain = (value: unknown): string => String(value || '').trim().toLowerCase();
const normalizePurpose = (value: unknown): AgentPurpose => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'cold_outreach') return 'cold_outreach';
  if (normalized === 'newsletter') return 'newsletter';
  return 'transactional';
};

const toTitleCase = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const COMMON_FIRST_NAME_PREFIXES = [
  'aaliyah', 'abigail', 'alex', 'alexander', 'alexandra', 'allison', 'alyssa', 'amanda', 'andrew',
  'anthony', 'ashley', 'austin', 'ava', 'benjamin', 'blake', 'brandon', 'brian', 'brielle', 'brittany',
  'brock', 'caleb', 'callie', 'camila', 'carlos', 'chris', 'christina', 'christopher', 'claire',
  'courtney', 'daniel', 'david', 'dean', 'derek', 'deshawn', 'diego', 'emily', 'emma', 'eric', 'evan',
  'gabriel', 'grace', 'hannah', 'isabella', 'jack', 'jacob', 'james', 'jason', 'jennifer', 'jessica',
  'john', 'jonathan', 'jordan', 'joseph', 'joshua', 'justin', 'kevin', 'kyle', 'lauren', 'liam', 'lucas',
  'madison', 'maria', 'mark', 'mason', 'matthew', 'megan', 'michael', 'natalie', 'nicholas', 'noah',
  'olivia', 'rachel', 'ryan', 'samuel', 'sarah', 'sofia', 'steven', 'taylor', 'thomas', 'victoria',
  'william', 'zoe',
].sort((a, b) => b.length - a.length);

const humanizeNameToken = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const withCapsSeparated = raw.replace(/([a-z])([A-Z])/g, '$1 $2');
  if (/\s/.test(withCapsSeparated)) return toTitleCase(withCapsSeparated);

  const lower = withCapsSeparated.toLowerCase();
  if (!/^[a-z]+$/.test(lower) || lower.length < 7) return toTitleCase(withCapsSeparated);

  const commonPrefix = COMMON_FIRST_NAME_PREFIXES.find(
    (prefix) => lower.startsWith(prefix) && (lower.length - prefix.length) >= 2
  );
  if (commonPrefix) {
    return toTitleCase(`${lower.slice(0, commonPrefix.length)} ${lower.slice(commonPrefix.length)}`);
  }

  let bestIndex: number | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;
  const target = Math.round(lower.length * 0.56);

  for (let index = 3; index <= lower.length - 3; index += 1) {
    const left = lower.slice(0, index);
    const right = lower.slice(index);
    if (!/[aeiouy]/.test(left) || !/[aeiouy]/.test(right)) continue;

    const startsStrong = /^(?:[bcdfghjklmnpqrstvwxyz][aeiouy]|ch|sh|th|ph|wh|qu|br|cr|dr|fr|gr|pr|tr|st|sl|sm|sn|sp|sw|cl|fl|gl|pl|bl|sc|sk|wr)/.test(right);
    const awkwardStart = /^(?:lr|nr|mr|sr|zr|nb|nm|md|dw|pt|tm|vg|xh|q)/.test(right);
    const score = (startsStrong ? 2 : 0) - (Math.abs(index - target) * 0.65) - (awkwardStart ? 2 : 0);

    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  }

  if (bestIndex != null && bestScore > -1.5) {
    return toTitleCase(`${lower.slice(0, bestIndex)} ${lower.slice(bestIndex)}`);
  }

  return toTitleCase(withCapsSeparated);
};

const deriveNameFromEmail = (email: string): string => {
  const local = String(email || '').split('@')[0] || '';
  const withoutAlias = local.split('+')[0];
  const prettified = withoutAlias.replace(/[._-]+/g, ' ').replace(/\d+/g, ' ').trim();
  return prettified ? humanizeNameToken(prettified) : email;
};

const normalizeUrl = (value: unknown): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const normalizeSection = (value: unknown): AgentSection | null => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'sales') return 'sales';
  if (normalized === 'account_manager' || normalized === 'account') return 'account_manager';
  if (normalized === 'system') return 'system';
  return null;
};

const SYSTEM_EMAIL_KEYWORDS = [
  'admin',
  'postmaster',
  'no-reply',
  'noreply',
  'do-not-reply',
  'donotreply',
  'mailer-daemon',
  'bounce',
  'support',
  'help',
  'notification',
  'notifications',
  'system',
  'security',
  'aureliadataworks',
];

const looksLikeSystemEmail = (email: string): boolean => {
  const normalizedEmail = normalizeEmail(email);
  const localPart = String(normalizedEmail.split('@')[0] || '');
  if (!localPart) return false;
  const localPartTokens = localPart.replace(/[._-]+/g, ' ').split(/\s+/).filter(Boolean);
  const localPartFlat = localPart.replace(/[._-]+/g, '');

  return SYSTEM_EMAIL_KEYWORDS.some((keyword) => {
    const keywordFlat = keyword.replace(/[-_]/g, '');
    if (localPart === keyword || localPartFlat === keywordFlat) return true;
    if (localPart.includes(keyword) || localPartFlat.includes(keywordFlat)) return true;
    return localPartTokens.includes(keyword);
  });
};

const getDefaultSectionFromEmailAndPurpose = (email: string, purpose: AgentPurpose): AgentSection => {
  if (looksLikeSystemEmail(email)) return 'system';
  if (purpose === 'cold_outreach') return 'sales';
  return 'account_manager';
};

const sanitizeProfile = (profile: AgentProfile): AgentProfile => {
  const firstName = String(profile.firstName || '').trim();
  const lastName = String(profile.lastName || '').trim();
  const explicitFull = String(profile.fullName || '').trim();
  const fullName = (firstName || lastName)
    ? `${firstName} ${lastName}`.trim()
    : explicitFull;
  return {
    firstName,
    lastName,
    fullName,
    title: String(profile.title || '').trim(),
    linkedInUrl: normalizeUrl(profile.linkedInUrl),
    websiteUrl: normalizeUrl(profile.websiteUrl),
    socialUrl: normalizeUrl(profile.socialUrl),
    phone: String(profile.phone || '').trim(),
    location: String(profile.location || '').trim(),
    bio: String(profile.bio || '').trim(),
  };
};

const hasAnyProfileValue = (profile: AgentProfile): boolean =>
  Object.values(profile).some((value) => String(value || '').trim().length > 0);

const getPurposeLabel = (purpose: AgentPurpose): string => {
  if (purpose === 'cold_outreach') return 'Cold Outreach';
  if (purpose === 'newsletter') return 'Newsletter';
  return 'Transactional';
};

const purposeBadgeVariant = (purpose: AgentPurpose): string => {
  if (purpose === 'cold_outreach') return 'primary';
  if (purpose === 'newsletter') return 'warning';
  return 'success';
};

const getSectionLabel = (section: AgentSection): string => {
  if (section === 'sales') return 'Sales Agent';
  if (section === 'account_manager') return 'Account Manager';
  return 'System';
};

const sectionBadgeVariant = (section: AgentSection): string => {
  if (section === 'sales') return 'primary';
  if (section === 'account_manager') return 'success';
  return 'dark';
};

const Agents: React.FC = () => {
  const accountContext = useAccount();
  const isAllAccountsSelected = accountContext?.isAllAccountsSelected;
  const selectedAccount = accountContext?.selectedAccount;
  const tenantIdForPrefs = useTenantIdForPreferences();

  const { data: emailAccountsData, isLoading: isLoadingAccounts } = useGetEmailAccountsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: domainsData, isLoading: isLoadingDomains } = useGetDomainsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: userPreferencesData, isLoading: isLoadingPrefs } = useGetUserPreferencesQuery(
    tenantIdForPrefs ? { tenantId: tenantIdForPrefs } : undefined,
    { refetchOnMountOrArgChange: true }
  );
  const isDataReady = !isLoadingAccounts && !isLoadingDomains && !isLoadingPrefs;
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [profilesByEmail, setProfilesByEmail] = useState<Record<string, AgentProfile>>({});
  const [sectionsByEmail, setSectionsByEmail] = useState<Record<string, AgentSection>>({});
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [profileDraft, setProfileDraft] = useState<AgentProfile>(EMPTY_PROFILE);
  const [sectionDraft, setSectionDraft] = useState<AgentSection>('sales');
  const [isSavingProfile, setIsSavingProfile] = useState<boolean>(false);
  const [didCopyEmail, setDidCopyEmail] = useState<boolean>(false);
  const [cardViewMode, setCardViewMode] = useState<AgentCardViewMode>(() => {
    try {
      const saved = localStorage.getItem('emmAgentsCardViewMode');
      if (saved === 'detailed') return 'detailed';
      if (saved === 'list') return 'list';
      return 'compact';
    } catch {
      return 'compact';
    }
  });

  useEffect(() => {
    const raw = (userPreferencesData as any)?.preferences?.emmAgentProfiles;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      setProfilesByEmail({});
      return;
    }
    const normalized: Record<string, AgentProfile> = {};
    Object.entries(raw as Record<string, any>).forEach(([emailRaw, profileRaw]) => {
      const email = normalizeEmail(emailRaw);
      if (!email || !profileRaw || typeof profileRaw !== 'object' || Array.isArray(profileRaw)) return;
      const sanitized = sanitizeProfile(profileRaw as AgentProfile);
      if (!hasAnyProfileValue(sanitized)) return;
      normalized[email] = sanitized;
    });
    setProfilesByEmail(normalized);
  }, [userPreferencesData]);

  useEffect(() => {
    const raw = (userPreferencesData as any)?.preferences?.emmAgentSections;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      setSectionsByEmail({});
      return;
    }
    const normalized: Record<string, AgentSection> = {};
    Object.entries(raw as Record<string, any>).forEach(([emailRaw, sectionRaw]) => {
      const email = normalizeEmail(emailRaw);
      const section = normalizeSection(sectionRaw);
      if (!email || !section) return;
      normalized[email] = section;
    });
    setSectionsByEmail(normalized);
  }, [userPreferencesData]);

  useEffect(() => {
    try {
      localStorage.setItem('emmAgentsCardViewMode', cardViewMode);
    } catch (error) {
      console.error('Failed to persist Agents view mode:', error);
    }
  }, [cardViewMode]);

  const domainPurposeByDomain = useMemo(() => {
    const result = new Map<string, AgentPurpose>();
    const rawDomains = ((domainsData as any)?.data?.domains || (domainsData as any)?.domains || []) as any[];
    rawDomains.forEach((domainObj) => {
      const domain = normalizeDomain(domainObj?.domain);
      if (!domain) return;
      result.set(domain, normalizePurpose(domainObj?.domainType || domainObj?.domain_type));
    });
    return result;
  }, [domainsData]);

  const selectedAccountDomains = useMemo<string[]>(() => {
    if (isAllAccountsSelected) return [] as string[];
    const rawDomains = Array.isArray(selectedAccount?.domains) ? (selectedAccount.domains as string[]) : [];
    return rawDomains.map((domain: string) => normalizeDomain(domain)).filter(Boolean);
  }, [isAllAccountsSelected, selectedAccount?.domains]);

  const scopedAgents = useMemo(() => {
    const rawAccounts = ((emailAccountsData as any)?.data?.emailAccounts || (emailAccountsData as any)?.emailAccounts || []) as any[];
    const dedup = new Map<string, AgentCard>();
    const resolvePurposeForDomain = (domain: string): AgentPurpose => {
      const normalized = normalizeDomain(domain);
      if (!normalized) return 'transactional';
      const exact = domainPurposeByDomain.get(normalized);
      if (exact) return exact;

      let fallback: AgentPurpose | null = null;
      let fallbackLength = 0;
      domainPurposeByDomain.forEach((purpose, domainKey) => {
        if (!domainKey) return;
        if (normalized === domainKey || normalized.endsWith(`.${domainKey}`)) {
          if (domainKey.length > fallbackLength) {
            fallback = purpose;
            fallbackLength = domainKey.length;
          }
        }
      });
      return fallback || 'transactional';
    };

    rawAccounts.forEach((account) => {
      const fullEmailCandidate = String(account?.fullEmail || account?.email || '').trim();
      const fullEmail = fullEmailCandidate.includes('@')
        ? fullEmailCandidate
        : `${String(account?.email || '').trim()}@${String(account?.domain || '').trim()}`;
      const email = normalizeEmail(fullEmail);
      if (!email || !email.includes('@')) return;

      const domain = normalizeDomain(email.split('@')[1] || account?.domain);
      if (!domain) return;
      if (selectedAccountDomains.length > 0) {
        const isInScope = selectedAccountDomains.some((scopeDomain: string) => (
          domain === scopeDomain || domain.endsWith(`.${scopeDomain}`)
        ));
        if (!isInScope) return;
      }

      const profile = profilesByEmail[email] || EMPTY_PROFILE;
      const profileFirstLast = `${String(profile.firstName || '').trim()} ${String(profile.lastName || '').trim()}`.trim();
      const accountFullName = `${String(account?.firstName || '').trim()} ${String(account?.lastName || '').trim()}`.trim();
      const accountDisplayName = String(account?.displayName || account?.name || '').trim();
      const fallbackAccountName = accountFullName || accountDisplayName;
      const fullName = profileFirstLast || String(profile.fullName || '').trim() || (fallbackAccountName ? humanizeNameToken(fallbackAccountName) : '') || deriveNameFromEmail(email);
      const purpose = resolvePurposeForDomain(domain);
      const sectionOverride = normalizeSection(sectionsByEmail[email]);
      const section = sectionOverride || getDefaultSectionFromEmailAndPurpose(email, purpose);
      dedup.set(email, {
        email,
        domain,
        avatarUrl: String(account?.avatar || account?.avatarUrl || '').trim(),
        purpose,
        section,
        fullName,
        profile,
      });
    });

    return Array.from(dedup.values()).sort((a, b) => (
      a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase())
    ));
  }, [domainPurposeByDomain, emailAccountsData, profilesByEmail, sectionsByEmail, selectedAccountDomains]);

  const filteredAgents = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase();
    if (!term) return scopedAgents;
    return scopedAgents.filter((agent) => {
      const haystack = [
        agent.fullName,
        agent.profile.firstName,
        agent.profile.lastName,
        agent.email,
        agent.domain,
        agent.profile.title,
        agent.profile.location,
        agent.profile.bio,
        getSectionLabel(agent.section),
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(term);
    });
  }, [scopedAgents, searchTerm]);

  const salesAgents = useMemo(
    () => filteredAgents.filter((agent) => agent.section === 'sales'),
    [filteredAgents]
  );
  const accountManagers = useMemo(
    () => filteredAgents.filter((agent) => agent.section === 'account_manager'),
    [filteredAgents]
  );
  const systemAgents = useMemo(
    () => filteredAgents.filter((agent) => agent.section === 'system'),
    [filteredAgents]
  );
  const selectedEditingAgent = useMemo(() => {
    const key = normalizeEmail(editingEmail);
    if (!key) return null;
    return scopedAgents.find((agent) => normalizeEmail(agent.email) === key) || null;
  }, [editingEmail, scopedAgents]);

  const openEditModal = (agent: AgentCard): void => {
    setEditingEmail(agent.email);
    setSectionDraft(agent.section);
    setDidCopyEmail(false);

    const hasExplicitFirstLast = Boolean(
      String(agent.profile.firstName || '').trim() || String(agent.profile.lastName || '').trim()
    );
    let firstName = String(agent.profile.firstName || '').trim();
    let lastName = String(agent.profile.lastName || '').trim();

    if (!hasExplicitFirstLast) {
      const displayName = String(agent.profile.fullName || '').trim() || agent.fullName;
      const parts = displayName.split(/\s+/).filter(Boolean);
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ') || '';
    }

    setProfileDraft({
      ...EMPTY_PROFILE,
      ...agent.profile,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim(),
    });
  };

  const handleCopyEditingEmail = async (): Promise<void> => {
    const emailToCopy = String(editingEmail || '').trim();
    if (!emailToCopy) return;
    try {
      await navigator.clipboard.writeText(emailToCopy);
      setDidCopyEmail(true);
      window.setTimeout(() => setDidCopyEmail(false), 1500);
    } catch (error) {
      console.error('Failed to copy agent email:', error);
      alert('Could not copy email. Please copy manually.');
    }
  };

  const handleDraftFieldChange = (field: keyof AgentProfile, value: string): void => {
    setProfileDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async (): Promise<void> => {
    if (!editingEmail) return;
    const emailKey = normalizeEmail(editingEmail);
    if (!emailKey) return;

    const currentAgent = scopedAgents.find((agent) => normalizeEmail(agent.email) === emailKey);
    const defaultSection = getDefaultSectionFromEmailAndPurpose(emailKey, currentAgent?.purpose || 'transactional');
    const sanitized = sanitizeProfile(profileDraft);
    const nextProfiles: Record<string, AgentProfile> = { ...profilesByEmail };
    const nextSections: Record<string, AgentSection> = { ...sectionsByEmail };

    if (hasAnyProfileValue(sanitized)) nextProfiles[emailKey] = sanitized;
    else delete nextProfiles[emailKey];
    if (sectionDraft === defaultSection) delete nextSections[emailKey];
    else nextSections[emailKey] = sectionDraft;

    setIsSavingProfile(true);
    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          emmAgentProfiles: nextProfiles,
          emmAgentSections: nextSections,
        },
      }).unwrap();
      setProfilesByEmail(nextProfiles);
      setSectionsByEmail(nextSections);
      setEditingEmail(null);
      setProfileDraft(EMPTY_PROFILE);
      setSectionDraft('sales');
      setDidCopyEmail(false);
    } catch (error: any) {
      console.error('Failed to save agent profile:', error);
      alert(`Failed to save agent profile: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const renderAgentCard = (agent: AgentCard) => {
    const initials = (agent.fullName || agent.email)
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'A';
    const profile = agent.profile || EMPTY_PROFILE;
    const hasLinks = Boolean(profile.linkedInUrl || profile.websiteUrl || profile.socialUrl);
    const cardBadgeLabel = agent.section === 'system' ? 'System' : getPurposeLabel(agent.purpose);
    const cardBadgeVariant = agent.section === 'system' ? 'dark' : purposeBadgeVariant(agent.purpose);

    if (cardViewMode === 'compact') {
      const compactSize = 48;
      return (
        <Col key={agent.email} xs={6} sm={4} md={3} lg={2} xxl={2}>
          <Card
            className="h-100 border-0 shadow-sm"
            style={{ borderRadius: '12px', cursor: 'pointer' }}
            onClick={() => openEditModal(agent)}
            title="Click to view and edit full profile"
          >
            <Card.Body className="p-2 d-flex flex-column align-items-center text-center">
              {agent.avatarUrl ? (
                <img
                  src={agent.avatarUrl}
                  alt={agent.fullName}
                  style={{
                    width: compactSize,
                    minWidth: compactSize,
                    maxWidth: compactSize,
                    height: compactSize,
                    minHeight: compactSize,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                    border: '2px solid #eef2ff',
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center text-white fw-semibold"
                  style={{
                    width: compactSize,
                    minWidth: compactSize,
                    maxWidth: compactSize,
                    height: compactSize,
                    minHeight: compactSize,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #0d6efd 0%, #5b8cff 100%)',
                    fontSize: '0.85rem',
                  }}
                >
                  {initials}
                </div>
              )}
              <div className="fw-semibold mt-2 text-truncate w-100" style={{ fontSize: '0.78rem' }}>
                {agent.fullName}
              </div>
              <Badge
                bg={cardBadgeVariant}
                className="mt-1"
                style={{ fontSize: '0.62rem', fontWeight: 600 }}
              >
                {cardBadgeLabel}
              </Badge>
            </Card.Body>
          </Card>
        </Col>
      );
    }

    return (
      <Col key={agent.email} md={6} xl={4}>
        <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '14px' }}>
          <Card.Body className="d-flex flex-column">
            <div className="d-flex align-items-start justify-content-between mb-3">
              <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                {agent.avatarUrl ? (
                  <img
                    src={agent.avatarUrl}
                    alt={agent.fullName}
                    style={{
                      width: 52,
                      minWidth: 52,
                      maxWidth: 52,
                      height: 52,
                      minHeight: 52,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                      marginRight: 12,
                      border: '2px solid #eef2ff',
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center text-white fw-semibold"
                    style={{
                      width: 52,
                      minWidth: 52,
                      maxWidth: 52,
                      height: 52,
                      minHeight: 52,
                      flexShrink: 0,
                      borderRadius: '50%',
                      marginRight: 12,
                      background: 'linear-gradient(135deg, #0d6efd 0%, #5b8cff 100%)',
                      fontSize: '0.85rem',
                    }}
                  >
                    {initials}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-truncate">{agent.fullName}</div>
                  <div className="text-muted small d-flex align-items-center text-truncate">
                    <EnvelopeAt size={12} className="me-1 flex-shrink-0" />
                    <span className="text-truncate">{agent.email}</span>
                  </div>
                </div>
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }}
                onClick={() => openEditModal(agent)}
                title="Edit agent card"
              >
                <PencilSquare size={12} />
              </Button>
            </div>

            <div className="d-flex flex-wrap gap-1 mb-2">
              <Badge bg={sectionBadgeVariant(agent.section)}>{getSectionLabel(agent.section)}</Badge>
              {agent.section === 'system' ? (
                <Badge bg={purposeBadgeVariant(agent.purpose)}>{getPurposeLabel(agent.purpose)} Relay</Badge>
              ) : null}
              <Badge bg="light" text="dark">@{agent.domain}</Badge>
              {profile.title ? <Badge bg="secondary">{profile.title}</Badge> : null}
            </div>

            {profile.location ? (
              <div className="text-muted small d-flex align-items-center mb-2">
                <GeoAlt size={12} className="me-1" />
                {profile.location}
              </div>
            ) : null}

            {profile.phone ? (
              <div className="text-muted small d-flex align-items-center mb-2">
                <Telephone size={12} className="me-1" />
                {profile.phone}
              </div>
            ) : null}

            <div className="text-muted small mb-3" style={{ minHeight: 38 }}>
              {profile.bio || 'Add social media, LinkedIn, and profile details for this agent.'}
            </div>

            <div className="mt-auto">
              {hasLinks ? (
                <div className="d-flex flex-wrap gap-2">
                  {profile.linkedInUrl ? (
                    <a href={profile.linkedInUrl} target="_blank" rel="noreferrer" className="small text-decoration-none">
                      <Linkedin size={12} className="me-1" />
                      LinkedIn
                    </a>
                  ) : null}
                  {profile.websiteUrl ? (
                    <a href={profile.websiteUrl} target="_blank" rel="noreferrer" className="small text-decoration-none">
                      <Globe2 size={12} className="me-1" />
                      Website
                    </a>
                  ) : null}
                  {profile.socialUrl ? (
                    <a href={profile.socialUrl} target="_blank" rel="noreferrer" className="small text-decoration-none">
                      <Link45deg size={12} className="me-1" />
                      Social
                    </a>
                  ) : null}
                </div>
              ) : (
                <div className="small text-muted">No social links yet</div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderAgentListTable = (agents: AgentCard[]) => {
    const avatarSize = 32;
    return (
      <Table hover responsive className="align-middle mb-4" style={{ fontSize: '0.82rem' }}>
        <thead>
          <tr style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <th style={{ fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Agent</th>
            <th style={{ fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Email</th>
            <th style={{ fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Domain</th>
            <th style={{ fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Role</th>
            <th style={{ fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Purpose</th>
            <th style={{ fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #dee2e6' }}>Links</th>
            <th style={{ fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #dee2e6', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {agents.map((agent) => {
            const initials = (agent.fullName || agent.email)
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part.charAt(0).toUpperCase())
              .join('') || 'A';
            const profile = agent.profile || EMPTY_PROFILE;
            const purposeLabel = agent.section === 'system' ? 'System' : getPurposeLabel(agent.purpose);
            const purposeVariant = agent.section === 'system' ? 'dark' : purposeBadgeVariant(agent.purpose);
            const linkCount = [profile.linkedInUrl, profile.websiteUrl, profile.socialUrl].filter(Boolean).length;

            return (
              <tr
                key={agent.email}
                style={{ cursor: 'pointer' }}
                onClick={() => openEditModal(agent)}
                title="Click to view and edit full profile"
              >
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {agent.avatarUrl ? (
                      <img
                        src={agent.avatarUrl}
                        alt={agent.fullName}
                        style={{
                          width: avatarSize,
                          height: avatarSize,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          flexShrink: 0,
                          border: '1.5px solid #eef2ff',
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center text-white fw-semibold"
                        style={{
                          width: avatarSize,
                          height: avatarSize,
                          flexShrink: 0,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #0d6efd 0%, #5b8cff 100%)',
                          fontSize: '0.7rem',
                        }}
                      >
                        {initials}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-semibold text-truncate">{agent.fullName}</div>
                      {profile.title ? (
                        <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>
                          {profile.title}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td>
                  <span className="text-muted text-truncate d-inline-block" style={{ maxWidth: 220 }}>
                    {agent.email}
                  </span>
                </td>
                <td>
                  <Badge bg="light" text="dark" style={{ fontSize: '0.72rem' }}>@{agent.domain}</Badge>
                </td>
                <td>
                  <Badge bg={sectionBadgeVariant(agent.section)} style={{ fontSize: '0.7rem' }}>
                    {getSectionLabel(agent.section)}
                  </Badge>
                </td>
                <td>
                  <Badge bg={purposeVariant} style={{ fontSize: '0.7rem' }}>
                    {purposeLabel}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    {profile.linkedInUrl ? (
                      <a
                        href={profile.linkedInUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="LinkedIn"
                      >
                        <Linkedin size={14} className="text-primary" />
                      </a>
                    ) : null}
                    {profile.websiteUrl ? (
                      <a
                        href={profile.websiteUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Website"
                      >
                        <Globe2 size={14} className="text-primary" />
                      </a>
                    ) : null}
                    {profile.socialUrl ? (
                      <a
                        href={profile.socialUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Social"
                      >
                        <Link45deg size={14} className="text-primary" />
                      </a>
                    ) : null}
                    {linkCount === 0 ? <span className="text-muted" style={{ fontSize: '0.72rem' }}>—</span> : null}
                  </div>
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{ fontSize: '0.65rem', padding: '0.1rem 0.3rem' }}
                    onClick={(e) => { e.stopPropagation(); openEditModal(agent); }}
                    title="Edit agent card"
                  >
                    <PencilSquare size={12} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <EMMSectionHeader title="Agents" showHelp />
      <Container className="mt-3 pb-4">
        <Row className="align-items-center g-2 mb-3">
          <Col xl={5} lg={12}>
            <div className="text-muted text-truncate" style={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
              Manage your outbound team cards with role grouping and social profile links.
            </div>
          </Col>
          <Col xl={7} lg={12}>
            <div className="d-flex align-items-center justify-content-xl-end flex-wrap gap-2">
              <div className="btn-group" role="group" aria-label="Agent card view mode">
                <Button
                  variant={cardViewMode === 'compact' ? 'primary' : 'outline-primary'}
                  size="sm"
                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                  onClick={() => setCardViewMode('compact')}
                >
                  Compact
                </Button>
                <Button
                  variant={cardViewMode === 'detailed' ? 'primary' : 'outline-primary'}
                  size="sm"
                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                  onClick={() => setCardViewMode('detailed')}
                >
                  Detailed
                </Button>
                <Button
                  variant={cardViewMode === 'list' ? 'primary' : 'outline-primary'}
                  size="sm"
                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                  onClick={() => setCardViewMode('list')}
                >
                  List
                </Button>
              </div>
              <InputGroup size="sm" style={{ width: '100%', maxWidth: '320px' }}>
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search agents by name, email, or role"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  style={{ fontSize: '0.8rem' }}
                />
              </InputGroup>
            </div>
          </Col>
        </Row>

        {!isDataReady ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status" />
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>Loading agents...</div>
          </div>
        ) : scopedAgents.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No agents found for the current account scope.
          </Alert>
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2 mt-3">
              <h6 className="mb-0">
                Sales Agents <span className="text-muted fw-normal">(Outreach Team)</span>
              </h6>
              <Badge bg="primary">{salesAgents.length}</Badge>
            </div>
            {salesAgents.length === 0 ? (
              <Alert variant="light">No cold outreach agents match your search.</Alert>
            ) : cardViewMode === 'list' ? (
              renderAgentListTable(salesAgents)
            ) : (
              <Row className="g-3 mb-4">{salesAgents.map((agent) => renderAgentCard(agent))}</Row>
            )}

            <div className="d-flex align-items-center justify-content-between mb-2 mt-1">
              <h6 className="mb-0">
                Account Managers <span className="text-muted fw-normal">(Transactional Team)</span>
              </h6>
              <Badge bg="success">{accountManagers.length}</Badge>
            </div>
            {accountManagers.length === 0 ? (
              <Alert variant="light">No transactional agents match your search.</Alert>
            ) : cardViewMode === 'list' ? (
              renderAgentListTable(accountManagers)
            ) : (
              <Row className="g-3 mb-4">{accountManagers.map((agent) => renderAgentCard(agent))}</Row>
            )}

            <div className="d-flex align-items-center justify-content-between mb-2 mt-1">
              <h6 className="mb-0">
                System <span className="text-muted fw-normal">(Operational / non-agent emails)</span>
              </h6>
              <Badge bg="dark">{systemAgents.length}</Badge>
            </div>
            {systemAgents.length === 0 ? (
              <Alert variant="light">No system emails match your search.</Alert>
            ) : cardViewMode === 'list' ? (
              renderAgentListTable(systemAgents)
            ) : (
              <Row className="g-3">{systemAgents.map((agent) => renderAgentCard(agent))}</Row>
            )}
          </>
        )}
      </Container>

      <Modal
        show={Boolean(editingEmail)}
        onHide={() => {
          setEditingEmail(null);
          setProfileDraft(EMPTY_PROFILE);
          setSectionDraft('sales');
          setDidCopyEmail(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '0.95rem' }}>
            Edit Agent Card
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Section</Form.Label>
            <Form.Select
              size="sm"
              value={sectionDraft}
              onChange={(event) => setSectionDraft(normalizeSection(event.target.value) || 'sales')}
            >
              <option value="sales">Sales Agents</option>
              <option value="account_manager">Account Managers</option>
              <option value="system">System</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Email</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                value={editingEmail || ''}
                readOnly
                plaintext={false}
                style={{ backgroundColor: '#f8f9fa' }}
              />
              <Button
                variant={didCopyEmail ? 'primary' : 'outline-secondary'}
                onClick={() => { void handleCopyEditingEmail(); }}
                disabled={!editingEmail}
                title={didCopyEmail ? 'Copied' : 'Copy email'}
                aria-label={didCopyEmail ? 'Copied' : 'Copy email'}
              >
                {didCopyEmail ? <Check2 size={14} /> : <Copy size={14} />}
              </Button>
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Relay Type</Form.Label>
            <Form.Control
              size="sm"
              value={selectedEditingAgent ? getPurposeLabel(selectedEditingAgent.purpose) : ''}
              readOnly
              plaintext={false}
              style={{ backgroundColor: '#f8f9fa' }}
            />
          </Form.Group>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>First Name</Form.Label>
                <Form.Control
                  size="sm"
                  value={profileDraft.firstName || ''}
                  onChange={(event) => handleDraftFieldChange('firstName', event.target.value)}
                  placeholder="e.g. Tyler"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Last Name</Form.Label>
                <Form.Control
                  size="sm"
                  value={profileDraft.lastName || ''}
                  onChange={(event) => handleDraftFieldChange('lastName', event.target.value)}
                  placeholder="e.g. McKenzie"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Title</Form.Label>
            <Form.Control
              size="sm"
              value={profileDraft.title || ''}
              onChange={(event) => handleDraftFieldChange('title', event.target.value)}
              placeholder="e.g. Senior Sales Agent"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>LinkedIn URL</Form.Label>
            <Form.Control
              size="sm"
              value={profileDraft.linkedInUrl || ''}
              onChange={(event) => handleDraftFieldChange('linkedInUrl', event.target.value)}
              placeholder="linkedin.com/in/..."
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Website URL</Form.Label>
            <Form.Control
              size="sm"
              value={profileDraft.websiteUrl || ''}
              onChange={(event) => handleDraftFieldChange('websiteUrl', event.target.value)}
              placeholder="example.com"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '0.75rem' }}>Social URL</Form.Label>
            <Form.Control
              size="sm"
              value={profileDraft.socialUrl || ''}
              onChange={(event) => handleDraftFieldChange('socialUrl', event.target.value)}
              placeholder="instagram.com/... or x.com/..."
            />
          </Form.Group>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Phone</Form.Label>
                <Form.Control
                  size="sm"
                  value={profileDraft.phone || ''}
                  onChange={(event) => handleDraftFieldChange('phone', event.target.value)}
                  placeholder="(555) 123-4567"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: '0.75rem' }}>Location</Form.Label>
                <Form.Control
                  size="sm"
                  value={profileDraft.location || ''}
                  onChange={(event) => handleDraftFieldChange('location', event.target.value)}
                  placeholder="Austin, TX"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.75rem' }}>Bio</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={profileDraft.bio || ''}
              onChange={(event) => handleDraftFieldChange('bio', event.target.value)}
              placeholder="Short profile description..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditingEmail(null);
              setProfileDraft(EMPTY_PROFILE);
              setSectionDraft('sales');
              setDidCopyEmail(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => { void handleSaveProfile(); }} disabled={isSavingProfile}>
            {isSavingProfile ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Agents;
